import { useUserStore } from '@/store/user';
import { ItemButton } from './ItemButton';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getClientsList, ResponseGetClientsList } from '@/api/getClientsList';
import i18n from '@/localisation/i18n';
import { FadeReplace } from '@/UI/animations/FadeReplace';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { deleteClient, SubmitDeleteClient } from '@/api/deleteClient';
import { EmptyAside } from './EmptyAside';
import { useDeviceType } from '@/hooks/useDeviceType';

export const ListClients = () => {
  const { isMobile } = useDeviceType();
  const { client } = useParams();

  const { clients, setClientStrings } = useUserStore();

  const { data, refetch, isFetching } = useQuery<ResponseGetClientsList>({
    queryKey: ['clients'],
    staleTime: 5 * 60 * 1000,
    queryFn: () => getClientsList(),
  });

  useEffect(() => {
    refetch();
  }, [refetch]);

  const { mutate } = useMutation({
    mutationFn: (data: SubmitDeleteClient) => deleteClient(data),
    onSuccess: () => {
      refetch();

      if (!isMobile) {
        window.location.href = '/';
      }
    },
  });

  useEffect(() => {
    if (data) {
      setClientStrings(data);
    }
  }, [data, setClientStrings]);

  return (
    <div className="mt-10">
      <div className="text-xs font-semibold text-additional">
        {i18n.t('clients')}
      </div>
      <div className="mt-6">
        {data?.length === 0 && !isFetching ? (
          <EmptyAside />
        ) : (
          <FadeReplace
            firstElement={
              <div className="-ml-2">
                <div className="skeleton mb-6 h-9 w-full rounded-lg !bg-[#1F1F1F80]"></div>
                <div className="skeleton mb-6 h-9 w-full rounded-lg !bg-[#1F1F1F80]"></div>
                <div className="skeleton mb-6 h-9 w-full rounded-lg !bg-[#1F1F1F80]"></div>
                <div className="skeleton mb-6 h-9 w-full rounded-lg !bg-[#1F1F1F80]"></div>
                <div className="skeleton mb-6 h-9 w-full rounded-lg !bg-[#1F1F1F80]"></div>
              </div>
            }
            secondElement={
              <div className="h-[calc(100vh-380px)] overflow-y-auto">
                {data?.map(item => {
                  const current = clients[item.id];
                  const hasNewSmer =
                    current?.smerIds.length > current?.smerViewed.size;

                  const hasNewDaybook =
                    current?.daybooksIds.length > current?.daybooksViewed.size;

                  return (
                    <ItemButton
                      key={item.id}
                      name={item.userName}
                      id={item.id}
                      hasNew={hasNewSmer || hasNewDaybook}
                      isSelected={client === item.id}
                      onDelete={() => {
                        mutate({
                          client: item.id,
                        });
                      }}
                    />
                  );
                })}
              </div>
            }
            showFirst={data === undefined}
          />
        )}
      </div>
    </div>
  );
};
